import React from "react";
import { Lexer } from "marked";

export function splitWords(text) {
	return String(text).split(/(\s+)/).filter(pedaco => pedaco !== "");
}

function wrapWords(text, prefix) {
	return splitWords(text).map((pedaco, i) =>
		/^\s+$/.test(pedaco) ? (
			pedaco
		) : (
			<span className="repondeur-word" key={prefix + "-" + i}>
				{pedaco}
			</span>
		)
	);
}

const ENTIDADES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'" };

function decodificar(texto) {
	return String(texto).replace(/&(?:amp|lt|gt|quot|#39);/g, entidade => ENTIDADES[entidade]);
}

// o marked entrega o texto dos tokens com as entidades básicas escapadas;
// como o React escapa por conta própria, decodifica antes de renderizar
function textoPlano(token) {
	return decodificar(token.text);
}

const PROTOCOLOS_SEGUROS = /^(https?:|mailto:|tel:)/i;

function hrefSeguro(href) {
	const limpo = String(href || "").trim();
	return PROTOCOLOS_SEGUROS.test(limpo) ? limpo : null;
}

function renderTexto(texto, animar, chave) {
	return animar ? wrapWords(texto, chave) : texto;
}

function renderInline(tokens, animar, prefixo) {
	return (tokens || []).map((token, i) => {
		const chave = prefixo + "-" + i;

		switch (token.type) {
			case "strong":
				return <strong key={chave}>{renderInline(token.tokens, animar, chave)}</strong>;
			case "em":
				return <em key={chave}>{renderInline(token.tokens, animar, chave)}</em>;
			case "del":
				return <del key={chave}>{renderInline(token.tokens, animar, chave)}</del>;
			case "codespan":
				return <code key={chave}>{decodificar(token.text)}</code>;
			case "br":
				return <br key={chave} />;
			case "link": {
				const href = hrefSeguro(token.href);
				const conteudo = token.tokens && token.tokens.length
					? renderInline(token.tokens, animar, chave)
					: renderTexto(textoPlano(token), animar, chave);
				if (!href) {
					return <React.Fragment key={chave}>{conteudo}</React.Fragment>;
				}
				return (
					<a key={chave} href={href} target="_blank" rel="noopener noreferrer">
						{conteudo}
					</a>
				);
			}
			case "escape":
				return <React.Fragment key={chave}>{renderTexto(textoPlano(token), animar, chave)}</React.Fragment>;
			case "text":
				if (token.tokens && token.tokens.length) {
					return <React.Fragment key={chave}>{renderInline(token.tokens, animar, chave)}</React.Fragment>;
				}
				return <React.Fragment key={chave}>{renderTexto(textoPlano(token), animar, chave)}</React.Fragment>;
			case "image":
			case "html":
				return <React.Fragment key={chave}>{renderTexto(token.text, animar, chave)}</React.Fragment>;
			default:
				return <React.Fragment key={chave}>{renderTexto(token.raw || "", animar, chave)}</React.Fragment>;
		}
	});
}

function renderBlocos(tokens, animar, prefixo) {
	return (tokens || []).map((token, i) => {
		const chave = prefixo + "-" + i;

		switch (token.type) {
			case "space":
				return null;
			case "paragraph":
				return <p key={chave}>{renderInline(token.tokens, animar, chave)}</p>;
			case "heading":
				return (
					<h4 className="repondeur-md-titulo" key={chave}>
						{renderInline(token.tokens, animar, chave)}
					</h4>
				);
			case "list": {
				const itens = token.items.map((item, j) => (
					<li key={chave + "-" + j}>{renderBlocos(item.tokens, animar, chave + "-" + j)}</li>
				));
				return token.ordered ? (
					<ol key={chave} start={token.start && token.start !== 1 ? token.start : undefined}>
						{itens}
					</ol>
				) : (
					<ul key={chave}>{itens}</ul>
				);
			}
			case "text":
				return (
					<React.Fragment key={chave}>
						{token.tokens && token.tokens.length
							? renderInline(token.tokens, animar, chave)
							: renderTexto(textoPlano(token), animar, chave)}
					</React.Fragment>
				);
			case "code":
				return (
					<pre key={chave}>
						<code>{decodificar(token.text)}</code>
					</pre>
				);
			case "blockquote":
				return <blockquote key={chave}>{renderBlocos(token.tokens, animar, chave)}</blockquote>;
			case "hr":
				return <hr key={chave} />;
			case "table": {
				const cabecalho = token.header.map((celula, j) => (
					<th key={j} style={celula.align ? { textAlign: celula.align } : undefined}>
						{renderInline(celula.tokens, animar, chave + "-h" + j)}
					</th>
				));
				const linhas = token.rows.map((linha, j) => (
					<tr key={j}>
						{linha.map((celula, k) => (
							<td key={k} style={celula.align ? { textAlign: celula.align } : undefined}>
								{renderInline(celula.tokens, animar, chave + "-" + j + "-" + k)}
							</td>
						))}
					</tr>
				));
				return (
					<table key={chave}>
						<thead>
							<tr>{cabecalho}</tr>
						</thead>
						<tbody>{linhas}</tbody>
					</table>
				);
			}
			case "html":
				return <p key={chave}>{renderTexto(token.text, animar, chave)}</p>;
			case "def":
				return null;
			default:
				return <p key={chave}>{renderTexto(token.raw || "", animar, chave)}</p>;
		}
	});
}

export function renderMarkdown(text, animar) {
	const tokens = Lexer.lex(String(text || ""), { gfm: true, breaks: true });
	return renderBlocos(tokens, animar, "md");
}
